import React, { useState, useRef, useLayoutEffect } from 'react';
import { Monitor, Tablet, Smartphone, X } from 'lucide-react';
import { SiteDocument, PageDocument, SectionInstance } from './types';
import { SectionRenderer } from './renderers';
import { InlineEditContext } from './InlineEditContext';

interface Props {
  siteDoc: SiteDocument;
  activePage: PageDocument | null;
  brandCssVars: React.CSSProperties;
  onClose: () => void;
}

const DEVICE_WIDTHS: Record<string, string> = {
  desktop: '100%',
  tablet: '768px',
  mobile: '375px',
};

const SIZE_MULT: Record<string, number> = { sm: 0.8, md: 1, lg: 1.25, xl: 1.5 };

// Mirrors SectionWrapper's background + text styling logic without any editor chrome.
const PreviewSectionWrapper: React.FC<{ section: SectionInstance; children: React.ReactNode }> = ({ section, children }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const injectedBg = useRef(false);

  useLayoutEffect(() => {
    const el = contentRef.current?.firstElementChild as HTMLElement | null;
    if (!el) return;
    const mode = section.controls.section_bg_mode || '';
    if (mode === 'solid' && section.controls.section_bg_color) {
      el.style.background = section.controls.section_bg_color;
      injectedBg.current = true;
    } else if (mode === 'gradient') {
      const gFrom = section.controls.section_gradient_from || 'var(--brand-primary, #1a365d)';
      const gTo   = section.controls.section_gradient_to   || 'var(--brand-secondary, #2563eb)';
      const gDir  = section.controls.section_gradient_direction || '135deg';
      el.style.background = `linear-gradient(${gDir}, ${gFrom} 0%, ${gTo} 100%)`;
      injectedBg.current = true;
    } else if (injectedBg.current) {
      el.style.background = '';
      injectedBg.current = false;
    }
  });

  const scopeClass = `sb-pv-${section.section_id.replace(/-/g, '')}`;
  const textColor = section.controls.section_text_color || '';
  const sizeMult = SIZE_MULT[section.controls.section_text_size] ?? null;
  const styleRules: string[] = [];
  if (textColor) {
    styleRules.push(
      `.${scopeClass} h1,.${scopeClass} h2,.${scopeClass} h3,.${scopeClass} h4,` +
      `.${scopeClass} p,.${scopeClass} li,.${scopeClass} span,.${scopeClass} a` +
      `{color:${textColor}!important}`
    );
  }
  if (sizeMult && sizeMult !== 1) {
    styleRules.push(`.${scopeClass} h1{font-size:calc(2.5rem*${sizeMult})!important}`);
    styleRules.push(`.${scopeClass} h2{font-size:calc(2rem*${sizeMult})!important}`);
    styleRules.push(`.${scopeClass} h3{font-size:calc(1.5rem*${sizeMult})!important}`);
    styleRules.push(`.${scopeClass} h4{font-size:calc(1.25rem*${sizeMult})!important}`);
    styleRules.push(`.${scopeClass} p,.${scopeClass} li{font-size:calc(1rem*${sizeMult})!important}`);
  }
  const btnBg = section.controls.section_button_color || '';
  const btnText = section.controls.section_button_text_color || '';
  if (btnBg) styleRules.push(`.${scopeClass} .sb-cta-btn{background:${btnBg}!important;border-color:${btnBg}!important}`);
  if (btnText) styleRules.push(`.${scopeClass} .sb-cta-btn{color:${btnText}!important}`);
  const scopedCss = styleRules.join('\n');

  return (
    <>
      {scopedCss && <style dangerouslySetInnerHTML={{ __html: scopedCss }} />}
      <div ref={contentRef} className={scopeClass}>{children}</div>
    </>
  );
};

const SitePreview: React.FC<Props> = ({ siteDoc, activePage, brandCssVars, onClose }) => {
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [previewPageId, setPreviewPageId] = useState<string>(activePage?.page_id || '');

  const page = siteDoc.pages.find(p => p.page_id === previewPageId) || activePage || siteDoc.pages[0];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column',
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 20px', background: '#1e293b', color: '#fff', flexShrink: 0,
      }}>
        <span style={{ fontWeight: 600, marginRight: 8 }}>Preview</span>

        {siteDoc.pages.length > 1 && (
          <select
            value={previewPageId}
            onChange={e => setPreviewPageId(e.target.value)}
            style={{
              background: '#334155', border: '1px solid #475569', color: '#fff',
              borderRadius: 6, padding: '4px 8px', fontSize: '0.8rem', cursor: 'pointer',
            }}
          >
            {siteDoc.pages.map(p => (
              <option key={p.page_id} value={p.page_id}>{p.title}</option>
            ))}
          </select>
        )}

        <div style={{ flex: 1 }} />

        {(['desktop', 'tablet', 'mobile'] as const).map(d => (
          <button key={d} onClick={() => setDevice(d)} style={{
            background: device === d ? 'rgba(255,255,255,0.2)' : 'transparent',
            border: 'none', color: '#fff', cursor: 'pointer', padding: '6px 10px', borderRadius: 6,
          }}>
            {d === 'desktop' && <Monitor size={18} />}
            {d === 'tablet' && <Tablet size={18} />}
            {d === 'mobile' && <Smartphone size={18} />}
          </button>
        ))}

        <button onClick={onClose} style={{
          background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer',
          padding: '6px 10px', marginLeft: 8,
        }}><X size={20} /></button>
      </div>

      {/* Preview area */}
      <div style={{
        flex: 1, display: 'flex', justifyContent: 'center',
        padding: device === 'desktop' ? 0 : 24,
        background: '#e2e8f0', overflow: 'auto',
      }}>
        <div style={{
          width: DEVICE_WIDTHS[device],
          maxWidth: '100%',
          background: '#fff',
          boxShadow: device === 'desktop' ? 'none' : '0 4px 24px rgba(0,0,0,0.15)',
          borderRadius: device === 'desktop' ? 0 : 8,
          overflowY: 'auto',
          ...brandCssVars,
        }}>
          {/* Mirror SITE_BASE_CSS font + reset rules so preview matches the Python-rendered output */}
          <style>{`
            .sb-preview-root *, .sb-preview-root *::before, .sb-preview-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
            .sb-preview-root img { max-width: 100%; height: auto; display: block; }
            .sb-preview-root body, .sb-preview-root { font-family: var(--brand-body-font, 'Inter', -apple-system, sans-serif); line-height: 1.6; -webkit-font-smoothing: antialiased; }
            .sb-preview-root h1, .sb-preview-root h2, .sb-preview-root h3,
            .sb-preview-root h4, .sb-preview-root h5, .sb-preview-root h6 {
              font-family: var(--brand-heading-font, 'Inter', -apple-system, sans-serif);
              font-weight: var(--brand-heading-weight, 700);
              line-height: var(--brand-heading-line-height, 1.15);
              letter-spacing: var(--brand-heading-letter-spacing, -0.025em);
            }
            .sb-preview-root h1 { font-size: var(--brand-hero-font-size, 3.75rem); font-weight: var(--brand-heading-weight, 800); line-height: var(--brand-heading-line-height, 1.08); }
            .sb-preview-root h2 { font-size: 2.5rem; }
            .sb-preview-root h3 { font-size: 1.375rem; }
            .sb-preview-root a { color: var(--brand-primary, #1a365d); text-decoration: none; }
          `}</style>
          {page ? (
            <InlineEditContext.Provider value={{
              sectionId: '',
              componentId: '',
              controls: {},
              onControlChange: () => {},
              isEditMode: false,
            }}>
              <div className="sb-preview-root">
                {page.sections.map(section => (
                  <PreviewSectionWrapper key={section.section_id} section={section}>
                    <SectionRenderer section={section} />
                  </PreviewSectionWrapper>
                ))}
              </div>
            </InlineEditContext.Provider>
          ) : (
            <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>
              No sections on this page
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SitePreview;
